import router from "@/router"
import { AccountService } from "@/services"
import store from "@/store"

/**
 * ABSTRACT ERROR HANDLING CLASS
 */

class BaseHandler {
  status = null
  intercept(error, options) {
    throw new Error("Not Implemented")
  }
}

class NotifyHandler extends BaseHandler {
  intercept(error, options = null) {
    const message = this.makeMessage(error, options || {})
    this.constructor.notify(message)
  }

  makeMessage(error, options) {
    throw new Error("Not Implemented")
  }

  static notify(message, color = "error") {
    store.commit("app/message", { msg: message, color })
  }
}

/**
 * CUSTOM ERRORS HANDLERS
 */

class EVoid extends BaseHandler {
  intercept(error, options) {
    return undefined
  }
}

class E400 extends NotifyHandler {
  status = 400
  makeMessage(error, options) {
    const data = error.response.data

    // Handle non field validation errors
    if (data.non_field_errors) {
      return data.non_field_errors.join("\n")
    }

    // Handle field validation errors
    return Object.entries(data)
      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
      .join("\n")
  }
}

/**
 * E401 - Force user logout
 */
class E401 extends BaseHandler {
  status = 401
  intercept(error, options) {
    AccountService.logout()
    router.push("/signin")
  }
}

class E403 extends NotifyHandler {
  status = 403
  makeMessage(error, options) {
    const data = error.response.data
    return data.detail || "Unauthorized"
  }
}

class E404 extends NotifyHandler {
  status = 404
  makeMessage(error, options) {
    const resource = options.resource || "Object"
    return `${resource} not found`
  }
}

class E413 extends NotifyHandler {
  status = 413
  makeMessage(error, options) {
    return "File is too large"
  }
}

class E422 extends NotifyHandler {
  status = 422
  makeMessage(error, options) {
    const data = error.response.data
    return data.default || "Unprocessable Entity"
  }
}

class E500 extends NotifyHandler {
  status = 500
  makeMessage(error, options) {
    return "Server Error"
  }
}

/**
 * GENERIC
 */

class ErrorHandlerCaller {
  #handlers = null

  constructor(handlers) {
    this.#handlers = Object.fromEntries(handlers.map(hdl => [hdl.status, hdl]))
  }

  intercept(error, options) {
    const status = error.response?.status
    const handler = this.#handlers[status] || new EVoid()
    return handler.intercept(error, options)
  }
}

const DEFAULTS_HANDLERS = [new E400(), new E403(), new E413(), new E422(), new E500()]

export { DEFAULTS_HANDLERS, E400, E401, E403, E404, E500, ErrorHandlerCaller }
